import type { ReactNode } from "react";
import { ExplainerDisclosure } from "./ExplainerDisclosure";

export interface MultiChoice {
  value: string;
  label: string;
  description?: string;
  explainerKey?: string;
}

interface Props {
  id: string;
  legend: string;
  choices: MultiChoice[];
  values: string[];
  onChange: (values: string[], message: string) => void;
  hint?: ReactNode;
  error?: string;
  /** At most one — see docs/design/features/W4-01.md "Density budget". */
  explainerKey?: string;
}

const EXCLUSIVE_VALUES = new Set(["not_working_or_studying", "none", "unsure"]);

export function MultiChoiceGroup({
  id,
  legend,
  choices,
  values,
  onChange,
  hint,
  error,
  explainerKey,
}: Props) {
  const describedBy = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""]
    .filter(Boolean)
    .join(" ") || undefined;

  const toggle = (choice: MultiChoice) => {
    const selected = values.includes(choice.value);
    let next = selected
      ? values.filter((value) => value !== choice.value)
      : [...values, choice.value];
    let message = "";

    if (!selected && EXCLUSIVE_VALUES.has(choice.value)) {
      next = [choice.value];
      message = `${choice.label} selected. Other choices cleared.`;
    } else if (!selected && next.some((value) => EXCLUSIVE_VALUES.has(value))) {
      next = next.filter((value) => !EXCLUSIVE_VALUES.has(value));
      message = `${choice.label} selected. The exclusive choice was cleared.`;
    }
    onChange(next, message);
  };

  return (
    <fieldset
      id={id}
      className={`choice-group ${error ? "field--error" : ""}`}
      aria-describedby={describedBy}
      aria-invalid={Boolean(error)}
    >
      <legend>{legend}</legend>
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
      {/* Not referenced by aria-describedby — see ChoiceGroup.tsx. Only
          rendered when a key is bound, so useContent() is never called
          without one, keeping every no-explainerKey render provider-free. */}
      {explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--stem" />}
      {error && <p id={`${id}-error`} className="field-error">{error}</p>}
      <div className="choices">
        {choices.map((choice) => (
          <div className="choice-row" key={choice.value}>
            <label className="choice choice--checkbox">
              <input
                type="checkbox"
                name={id}
                value={choice.value}
                checked={values.includes(choice.value)}
                aria-describedby={describedBy}
                aria-invalid={Boolean(error)}
                onChange={() => toggle(choice)}
              />
              <span className="choice__control" aria-hidden="true" />
              <span className="choice__copy">
                <strong>{choice.label}</strong>
                {choice.description && <small>{choice.description}</small>}
              </span>
            </label>
            {/* Sibling of the label, never inside it — see ChoiceGroup.tsx.
                Only rendered when a key is bound. */}
            {choice.explainerKey && (
              <ExplainerDisclosure explainerKey={choice.explainerKey} className="explainer--option" />
            )}
          </div>
        ))}
      </div>
    </fieldset>
  );
}
